import React, { useState, useEffect, useCallback } from 'react';
import { api } from './lib/api';
import TopBar from './components/TopBar';
import LeftPanel from './components/LeftPanel';
import CenterPanel from './components/CenterPanel';
import RightPanel from './components/RightPanel';
import OrionDashboard from './components/OrionDashboard';
import ShowDashboard from './components/ShowDashboard';
import VegaDashboard from './components/VegaDashboard';
import AtlasDashboard from './components/AtlasDashboard';
import AtlasProTerminal from './components/atlas/AtlasProTerminal';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import KarbonDashboard from './components/KarbonDashboard';
import InternalDeltaDashboard from './components/InternalDeltaDashboard';
import InternalProducerDashboard from './components/InternalProducerDashboard';
import StudioShow from './components/show/StudioShow';
import StudioPlaceholder from './components/show/StudioPlaceholder';
import FeedsMode from './components/show/FeedsMode';
import IntelMode from './components/show/IntelMode';
import CrewStudio from './components/crew/CrewStudio';
import QuillStudio from './components/quill/QuillStudio';
import NovaForecastPanel from './components/nova/NovaForecastPanel';
import ShowRoom from './components/show/ShowRoom';
import LiveTalkButton from './components/voice/LiveTalkButton';
import { getSavedUnit, saveUnit } from './utils/temperature';
import { AuthProvider } from './auth/AuthContext';
import AuthGate from './auth/AuthGate';
import Settings from './auth/Settings';
import GlobalControls from './components/GlobalControls';
import internalNavConfig from './internal/navConfig.json';
import './auth/http'; // installs global axios interceptors
import './App.css';
import './Orion.css';
import './Show.css';
import './Vega.css';
import './Atlas.css';
import './Analytics.css';
import './signal-global.css';
import './signal-map.css';
import './components/internalDashboards.css';

const INTERNAL_ROUTE_MIGRATIONS = {
  'internal-state-bus': 'internal-delta',
  'internal-sensor-ops': 'internal-delta',
  'internal-observability': 'internal-producer',
  'internal-topics': 'internal-producer',
};

const AGENT_TO_PATH = {
  nova: '/',
  orion: '/orion',
  vega: '/vega',
  atlas: '/atlas',
  karbon: '/karbon',
  analytics: '/analytics',
  show: '/studio/show',
  feeds: '/studio/feeds',
  intel: '/studio/intel',
  panel: '/studio/panel',
  archive: '/studio/archive',
  crew: '/crew',
  quill: '/quill',
  showroom: '/studio/showroom',
  'internal-delta': '/internal/delta',
  'internal-producer': '/internal/producer',
};

const PATH_TO_AGENT = {
  '/': 'nova',
  '/nova': 'nova',
  '/orion': 'orion',
  '/vega': 'vega',
  '/atlas': 'atlas',
  '/karbon': 'karbon',
  '/analytics': 'analytics',
  '/show': 'show',
  '/studio/show': 'show',
  '/studio/feeds': 'feeds',
  '/studio/intel': 'intel',
  '/studio/panel': 'panel',
  '/studio/archive': 'archive',
  '/crew': 'crew',
  '/studio/crew': 'crew',
  '/quill': 'quill',
  '/showroom': 'showroom',
  '/studio/showroom': 'showroom',
  '/internal/delta': 'internal-delta',
  '/internal/producer': 'internal-producer',
};

// ── Global Navigation Context ──────────────────────────────
const NavigationContext = React.createContext(null);

function useNavigation() {
  return React.useContext(NavigationContext);
}

function NavigationProvider({ children }) {
  const resolveAgentFromPath = useCallback(() => {
    if (typeof window === 'undefined') return 'nova';
    const path = (window.location.pathname || '/').replace(/\/+$/, '') || '/';
    return INTERNAL_ROUTE_MIGRATIONS[PATH_TO_AGENT[path] || 'nova'] || PATH_TO_AGENT[path] || 'nova';
  }, []);
  const [history, setHistory] = useState([resolveAgentFromPath()]);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const current = history[history.length - 1];

  const syncPath = useCallback((agent, { replace = false } = {}) => {
    if (typeof window === 'undefined') return;
    const target = INTERNAL_ROUTE_MIGRATIONS[agent] || agent;
    const nextPath = AGENT_TO_PATH[target] || AGENT_TO_PATH.nova;
    if (window.location.pathname === nextPath) return;
    if (replace) window.history.replaceState({}, '', nextPath);
    else window.history.pushState({}, '', nextPath);
  }, []);

  const navigate = useCallback((agent) => {
    const target = INTERNAL_ROUTE_MIGRATIONS[agent] || agent;
    setHistory((h) => {
      if (h[h.length - 1] === target) return h;
      syncPath(target);
      return [...h, target];
    });
  }, [syncPath]);

  const goBack = useCallback(() => {
    setHistory((h) => {
      if (h.length <= 1) return h;
      const next = h.slice(0, -1);
      syncPath(next[next.length - 1], { replace: true });
      return next;
    });
  }, [syncPath]);

  useEffect(() => {
    syncPath(current, { replace: true });
  }, [current, syncPath]);

  const canGoBack = history.length > 1;

  const value = {
    current,
    navigate,
    goBack,
    canGoBack,
    settingsOpen,
    openSettings: () => setSettingsOpen(true),
    closeSettings: () => setSettingsOpen(false),
  };

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

// Expose navigation helpers on window for TopBar components to call without drilling
function NavigationBridge() {
  const nav = useNavigation();
  useEffect(() => {
    window.__signalNav = nav;
    return () => { if (window.__signalNav === nav) delete window.__signalNav; };
  }, [nav]);
  useEffect(() => {
    // Reflect canGoBack on <body> so CSS can leave room for the floating back button
    const cls = 'signal-has-back';
    if (nav.canGoBack) document.body.classList.add(cls);
    else document.body.classList.remove(cls);
    return () => document.body.classList.remove(cls);
  }, [nav.canGoBack]);
  return null;
}

function NovaDashboard({ onSwitchAgent, onSwitchToShow, onSwitchToVega, onSwitchToAtlas, onSwitchToCrew, onSwitchToQuill, onSwitchToShowRoom, onSwitchToKarbon }) {
  const [worldState, setWorldState] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedRegionId, setSelectedRegionId] = useState(null);
  const [regionDetail, setRegionDetail] = useState(null);
  const [sourceHealth, setSourceHealth] = useState(null);
  const [tempUnit, setTempUnit] = useState(getSavedUnit);
  const [loading, setLoading] = useState(true);
  const [mobileView, setMobileView] = useState('map'); // 'map' | 'feed' | 'detail'
  const [forecastOpen, setForecastOpen] = useState(false);

  const fetchState = useCallback(async () => {
    try {
      const [stateRes, healthRes] = await Promise.all([
        api.get('/api/nova/state'),
        api.get('/api/nova/health/sources').catch(() => ({ data: null })),
      ]);
      setWorldState(stateRes.data);
      if (healthRes.data) setSourceHealth(healthRes.data);
      if (!selectedEventId && stateRes.data.events?.length > 0) {
        const top = stateRes.data.events.reduce((a, b) => a.severity > b.severity ? a : b);
        setSelectedEventId(top.id);
      }
    } catch (err) {
      console.error('Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedEventId]);

  useEffect(() => {
    fetchState();
    const interval = setInterval(fetchState, 30000);
    return () => clearInterval(interval);
  }, [fetchState]);

  useEffect(() => {
    if (!selectedRegionId) {
      setRegionDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/api/nova/regions/${selectedRegionId}`);
        if (!cancelled) setRegionDetail(res.data);
      } catch {
        if (!cancelled) setRegionDetail(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedRegionId]);

  const handleRefresh = useCallback(async () => {
    try {
      await api.post('/api/nova/refresh');
      await fetchState();
      if (selectedRegionId) {
        const res = await api.get(`/api/nova/regions/${selectedRegionId}`);
        setRegionDetail(res.data);
      }
    } catch {}
  }, [fetchState, selectedRegionId]);

  const handleSelectRegion = useCallback((regionId) => {
    setSelectedRegionId(prev => prev === regionId ? null : regionId);
  }, []);

  const handleToggleUnit = useCallback(() => {
    setTempUnit(prev => {
      const next = prev === 'F' ? 'C' : 'F';
      saveUnit(next);
      return next;
    });
  }, []);

  const selectedEvent = worldState?.events?.find(e => e.id === selectedEventId) || null;

  if (loading) {
    return (
      <div className="nova-loading" data-testid="loading-screen">
        <div className="loading-ring"></div>
        <div className="loading-text">Nova</div>
        <div className="loading-sub">Connecting to 15 sensing regions...</div>
      </div>
    );
  }

  if (!worldState) {
    return (
      <div className="nova-loading" data-testid="error-screen">
        <div className="loading-text" style={{ color: 'var(--severe)' }}>CONNECTION LOST</div>
        <div className="loading-sub">Unable to reach Nova Brain</div>
      </div>
    );
  }

  return (
    <div className="nova-app" data-testid="nova-app">
      {forecastOpen && (
        <NovaForecastPanel tempUnit={tempUnit} onClose={() => setForecastOpen(false)} />
      )}
      {!forecastOpen && (
      <>
      <TopBar
        timestamp={worldState.timestamp}
        sensorCount={worldState.sensor_count}
        totalEvents={worldState.total_event_count}
        regionsOnline={worldState.regions_online}
        regionsTotal={worldState.regions_total}
        onRefresh={handleRefresh}
        sourceHealth={sourceHealth}
        tempUnit={tempUnit}
        onToggleUnit={handleToggleUnit}
        onSwitchAgent={onSwitchAgent}
        onSwitchToShow={onSwitchToShow}
        onSwitchToVega={onSwitchToVega}
        onSwitchToAtlas={onSwitchToAtlas}
        onSwitchToCrew={onSwitchToCrew}
        onSwitchToQuill={onSwitchToQuill}
        onSwitchToShowRoom={onSwitchToShowRoom}
        onSwitchToKarbon={onSwitchToKarbon}
        onOpenForecast={() => setForecastOpen(true)}
      />
      <div className="mobile-view-tabs" data-testid="mobile-view-tabs">
        <button className={mobileView === 'map' ? 'active' : ''} onClick={() => setMobileView('map')} data-testid="mobile-tab-map">MAP</button>
        <button className={mobileView === 'feed' ? 'active' : ''} onClick={() => setMobileView('feed')} data-testid="mobile-tab-feed">FEED</button>
        <button className={mobileView === 'detail' ? 'active' : ''} onClick={() => setMobileView('detail')} data-testid="mobile-tab-detail">DETAIL</button>
      </div>
      <div className={`nova-body mobile-view-${mobileView}`}>
        <LeftPanel
          state={worldState.state}
          priorCalls={worldState.prior_calls}
          regions={worldState.regions}
          sourceHealth={sourceHealth}
          selectedRegionId={selectedRegionId}
          onSelectRegion={handleSelectRegion}
          tempUnit={tempUnit}
        />
        <CenterPanel
          events={worldState.events}
          regions={worldState.regions}
          selectedEventId={selectedEventId}
          selectedRegionId={selectedRegionId}
          onSelectEvent={setSelectedEventId}
          onSelectRegion={handleSelectRegion}
        />
        <RightPanel
          event={selectedEvent}
          events={worldState.events}
          selectedEventId={selectedEventId}
          onSelectEvent={setSelectedEventId}
          selectedRegionId={selectedRegionId}
          regionDetail={regionDetail}
          tempUnit={tempUnit}
        />
      </div>
      </>
      )}
      <LiveTalkButton agent="nova" label="Talk to Nova" />
    </div>
  );
}

function AppRouter() {
  const nav = useNavigation();
  const { settingsOpen, closeSettings } = nav;
  const activeAgent = nav.current;
  const internalViews = Array.isArray(internalNavConfig) ? internalNavConfig : [];
  const resolvedAgent = INTERNAL_ROUTE_MIGRATIONS[activeAgent] || activeAgent;
  const internalEntry = internalViews.find((view) => view.id === resolvedAgent);
  const internalRoutes = {
    'internal-delta': InternalDeltaDashboard,
    'internal-producer': InternalProducerDashboard,
    'internal-state-bus': InternalDeltaDashboard,
    'internal-sensor-ops': InternalDeltaDashboard,
    'internal-observability': InternalProducerDashboard,
    'internal-topics': InternalProducerDashboard,
  };

  const setActiveAgent = (a) => nav.navigate(a);

  let screen;
  if (internalEntry && internalRoutes[resolvedAgent]) {
    const InternalView = internalRoutes[resolvedAgent];
    screen = <InternalView />;
  } else if (activeAgent === 'show') {
    screen = (
      <StudioShow
        mode="show"
        onNavigate={(m) => setActiveAgent(m === 'admin' ? 'internal-delta' : m === 'show' ? 'show' : m)}
        onSwitchAgent={(a) => setActiveAgent(a)}
      />
    );
  } else if (activeAgent === 'feeds') {
    screen = <FeedsMode onNavigate={(m) => setActiveAgent(m === 'admin' ? 'internal-delta' : m)} />;
  } else if (activeAgent === 'intel') {
    screen = <IntelMode onNavigate={(m) => setActiveAgent(m === 'admin' ? 'internal-delta' : m)} />;
  } else if (activeAgent === 'panel' || activeAgent === 'archive') {
    screen = (
      <StudioPlaceholder
        mode={activeAgent}
        onNavigate={(m) => setActiveAgent(m === 'admin' ? 'internal-delta' : m)}
      />
    );
  } else if (activeAgent === 'show-legacy') {
    screen = <ShowDashboard onSwitchAgent={(agent) => setActiveAgent(agent)} />;
  } else if (activeAgent === 'orion') {
    screen = (
      <OrionDashboard
        onSwitchAgent={() => setActiveAgent('nova')}
        onSwitchToShow={() => setActiveAgent('show')}
        onSwitchToVega={() => setActiveAgent('vega')}
        onSwitchToAtlas={() => setActiveAgent('atlas')}
        onSwitchToCrew={() => setActiveAgent('crew')}
      />
    );
  } else if (activeAgent === 'vega') {
    screen = (
      <VegaDashboard
        onSwitchToNova={() => setActiveAgent('nova')}
        onSwitchToOrion={() => setActiveAgent('orion')}
        onSwitchToShow={() => setActiveAgent('show')}
        onSwitchToAtlas={() => setActiveAgent('atlas')}
        onSwitchToCrew={() => setActiveAgent('crew')}
      />
    );
  } else if (activeAgent === 'atlas') {
    screen = (
      <AtlasProTerminal
        onSwitchAgent={(a) => setActiveAgent(a)}
        onSwitchToNova={() => setActiveAgent('nova')}
        onSwitchToOrion={() => setActiveAgent('orion')}
        onSwitchToVega={() => setActiveAgent('vega')}
        onSwitchToShow={() => setActiveAgent('show')}
        onSwitchToCrew={() => setActiveAgent('crew')}
      />
    );
  } else if (activeAgent === 'karbon') {
    screen = <KarbonDashboard onSwitchTo={{
      nova: () => setActiveAgent('nova'),
      orion: () => setActiveAgent('orion'),
      vega: () => setActiveAgent('vega'),
      atlas: () => setActiveAgent('atlas'),
    }} />;
  } else if (activeAgent === 'analytics') {
    screen = <AnalyticsDashboard onSwitchAgent={(a) => setActiveAgent(a)} />;
  } else if (activeAgent === 'crew') {
    screen = <CrewStudio onNavigate={(m) => setActiveAgent(m)} />;
  } else if (activeAgent === 'quill') {
    screen = <QuillStudio onNavigate={(m) => setActiveAgent(m)} />;
  } else if (activeAgent === 'showroom') {
    screen = <ShowRoom onNavigate={(m) => setActiveAgent(m)} />;
  } else {
    screen = <NovaDashboard onSwitchAgent={() => setActiveAgent('orion')} onSwitchToShow={() => setActiveAgent('show')} onSwitchToVega={() => setActiveAgent('vega')} onSwitchToAtlas={() => setActiveAgent('atlas')} onSwitchToCrew={() => setActiveAgent('crew')} onSwitchToQuill={() => setActiveAgent('quill')} onSwitchToShowRoom={() => setActiveAgent('showroom')} onSwitchToKarbon={() => setActiveAgent('karbon')} />;
  }

  return (
    <>
      {screen}
      <GlobalControls />
      {settingsOpen && <Settings onClose={closeSettings} />}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <AuthGate>
        <NavigationProvider>
          <NavigationBridge />
          <AppRouter />
        </NavigationProvider>
      </AuthGate>
    </AuthProvider>
  );
}

export default App;
export { useNavigation };
